import { resolveBaseUrl } from './config.js';
import { ApiHttpError, handleResponse } from './errors.js';
async function tryRefreshAndRetry(adapter, doFetch) {
    const refreshToken = adapter.getRefreshToken?.();
    if (!refreshToken || !adapter.refreshAccessToken || !adapter.persistTokens) {
        return null;
    }
    try {
        const refreshRes = await adapter.refreshAccessToken(refreshToken);
        adapter.persistTokens(refreshRes);
        const { data: retryData } = await doFetch();
        adapter.onTokenRefreshed?.();
        return retryData;
    }
    catch {
        return null;
    }
}
async function handleAuthFailure(authAdapter, error, method, url, isAuthExempt, doFetch) {
    const err = error;
    const isAuthError = !!(err && (err.status === 401 || err.isAuthError));
    if (!isAuthError || isAuthExempt) {
        return null;
    }
    const retryData = await tryRefreshAndRetry(authAdapter, doFetch);
    if (retryData !== null) {
        return retryData;
    }
    const refreshToken = authAdapter.getRefreshToken?.();
    if (refreshToken) {
        authAdapter.clearTokens?.('refresh_failed');
    }
    else {
        authAdapter.clearTokens?.('no_refresh_token');
    }
    if (err instanceof ApiHttpError) {
        authAdapter.onAuthFailure?.(err, `${method} ${url}`);
    }
    return null;
}
export function createApiClient(options) {
    const baseUrl = resolveBaseUrl(options);
    const { authAdapter, reportError } = options;
    const apiRequest = async (config) => {
        const { url, method, data } = config;
        const isAuthExempt = authAdapter.isAuthExempt?.(url) ?? false;
        const doFetch = async () => {
            const headers = {
                ...authAdapter.getAuthHeader(url),
                ...(data ? { 'Content-Type': 'application/json' } : {}),
            };
            const response = await fetch(`${baseUrl}${url}`, {
                method,
                headers,
                ...(data ? { body: JSON.stringify(data) } : {}),
            });
            const tokenExpired = response.headers.get('X-Token-Expired') === 'true';
            const responseData = await handleResponse(response, url);
            return { data: responseData, tokenExpired };
        };
        try {
            const { data: responseData, tokenExpired } = await doFetch();
            if (tokenExpired && !isAuthExempt) {
                const retryData = await tryRefreshAndRetry(authAdapter, doFetch);
                if (retryData !== null) {
                    return retryData;
                }
            }
            return responseData;
        }
        catch (error) {
            const recovered = await handleAuthFailure(authAdapter, error, method, url, isAuthExempt, doFetch);
            if (recovered !== null) {
                return recovered;
            }
            reportError?.(error, `${method} ${url}`);
            throw error;
        }
    };
    const api = {
        get: (url) => apiRequest({ url, method: 'GET' }),
        post: (url, data) => apiRequest({ url, method: 'POST', data }),
        patch: (url, data) => apiRequest({ url, method: 'PATCH', data }),
        put: (url, data) => apiRequest({ url, method: 'PUT', data }),
        delete: (url, data) => apiRequest({ url, method: 'DELETE', data }),
    };
    return { baseUrl, apiRequest, api };
}
